import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ErrorBox } from './DataTable';

type Rows = Awaited<ReturnType<typeof api.listCarChanges>>['rows'];

const ACTION: Record<Rows[number]['action'], string> = { create: '등록', update: '수정', delete: '삭제', file: '서류' };
const show = (v: string | null) => (v === null ? '(빈칸)' : v);

/**
 * 차량 변경기록(#194 함남열) — 기준정보 › 차량 수정 창 맨 아래.
 * 누가·언제·어느 칸을 무엇에서 무엇으로 바꿨는지. 서류(등록증·보험증) 올림도 같은 줄에 섞어 보인다.
 * 기록은 2026-10-12 웹 저장부터(레거시 CarLog 는 이관하지 않았다).
 */
export default function CarChangePanel({ carId, custId }: { carId?: number; custId?: number }) {
  const [rows, setRows] = useState<Rows | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);

  // 거래처(10-30)도 같은 표·같은 모양 — custId 를 주면 거래처 기록
  useEffect(() => {
    setRows(null);
    setError(null);
    (custId ? api.listCustChanges({ custId }) : api.listCarChanges({ carId: carId! }))
      .then((r) => setRows(r.rows)).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [carId, custId]);

  if (error) return <ErrorBox message={error} />;
  const shown = rows && !all ? rows.slice(0, 5) : rows;
  return (
    <section className="space-y-2 rounded-xl border border-line p-3">
      <div className="flex items-center gap-2">
        <div className="font-semibold">변경기록</div>
        <span className="text-xs text-ink-soft">{rows ? `${rows.length}건` : '불러오는 중…'}</span>
      </div>
      {rows && rows.length === 0 && <p className="text-sm text-ink-faint">아직 변경기록이 없습니다.</p>}
      {shown && shown.length > 0 && (
        <div className="max-h-80 overflow-auto rounded-lg border border-line">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="sticky top-0 bg-canvas text-xs text-ink-soft">
              <tr>{['일시', '변경한 사람', '구분', '바뀐 내용'].map((t) => <th key={t} className="bg-canvas px-2 py-1 text-left">{t}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-line">
              {shown.map((r, i) => (
                <tr key={i} className="align-top">
                  <td className="whitespace-nowrap px-2 py-1">{r.at}</td>
                  <td className="whitespace-nowrap px-2 py-1">{r.who ?? '—'}</td>
                  <td className="whitespace-nowrap px-2 py-1">{ACTION[r.action]}</td>
                  <td className="px-2 py-1">
                    {r.action === 'delete' ? (custId ? '거래처를 지움' : '차량을 지움')
                      : r.action === 'create' ? '새로 등록'
                      : r.changes.map((c, j) => (
                        <div key={j}>
                          <span className="font-medium">{c.label}</span>{' '}
                          {r.action === 'file' ? <span>{c.new}</span>
                            : <span><span className="text-ink-soft line-through">{show(c.old)}</span> → {show(c.new)}</span>}
                        </div>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows && rows.length > 5 && (
        <button type="button" className="text-sm text-brand-700 underline" onClick={() => setAll((v) => !v)}>
          {all ? '최근 5건만' : `전체 ${rows.length}건 보기`}
        </button>
      )}
    </section>
  );
}
